
import { Injectable } from "@angular/core";
import { ResourceManager } from "../../../../classes/general/resource-manager";
import { TimeoutService } from "../timeout";
import { BehaviorSubject } from "rxjs";
import { ECheckActivityResult, IActivity, IQuestActivityDef } from "../../../../classes/def/core/activity";
import { ITimeoutMonitorParams, ITimeoutMonitorData, ETimeoutStatus } from 'src/app/classes/general/timeout';
import { IQuestActivityStatus, IQuestAction, IQuestOptions, EQuestDynamicType } from 'src/app/classes/def/activity/quest';
import { IActivityQuestSpecs, IActivityQuestResponse } from 'src/app/classes/def/nav-params/activity-details';
import { MiscDataService } from 'src/app/services/data/misc';
import { QuestUtils } from './quest-utils';
import { LocationApiService } from 'src/app/services/location/location-api';

import { LocationApiHelpersService } from 'src/app/services/location/location-api-helpers';
import { ActivitiesDataService } from 'src/app/services/data/activities';
import { UiExtensionService } from "src/app/services/general/ui/ui-extension";
import { ILatLng } from "src/app/classes/def/map/coords";
import { Messages } from "src/app/classes/def/app/messages";
import { DeepCopy } from "src/app/classes/general/deep-copy";


@Injectable({
    providedIn: 'root'
})
export class QuestActivityService {

    subscription = {
        timeoutActivityMonitor: null
    };

    params: IQuestActivityDef;
    moduleName: string = "QUEST ACTIVITY > ";
    statusObservable: BehaviorSubject<IQuestActivityStatus>;

    questData: IActivityQuestSpecs;
    multiQuestData: IActivityQuestSpecs[] = [];
    questStatus: IQuestActivityStatus;

    options: IQuestOptions = {
        /** percent */
        cluePenalty: 25,
        /** percent */
        mapCluePenalty: 50
    };

    activityFinished: boolean = false;

    constructor(
        public timeoutMonitor: TimeoutService,
        public misc: MiscDataService,
        public locationApi: LocationApiService,
        public locationApiHelpers: LocationApiHelpersService,
        public activityData: ActivitiesDataService,
        public uiext: UiExtensionService
    ) {
        console.log("quest activity service created");
        this.statusObservable = new BehaviorSubject(null);
    }

    watchStatus() {
        return this.statusObservable;
    }

    /**
     * get nearby street name
     * @param checkpoint 
     */
    requestGeocodeStreetNameOptions(checkpoint: ILatLng): Promise<string[]> {
        return new Promise((resolve, reject) => {
            if (checkpoint == null) {
                reject(new Error("undefined geocode coords"));
                return;
            }
            this.locationApi.reverseGeocode(checkpoint).then((res: google.maps.GeocoderResult[]) => {
                this.locationApiHelpers.getStreetNames(res).then((streetNames: string[]) => {
                    resolve(streetNames);
                }).catch((err) => {
                    reject(err);
                });
            }).catch((err) => {
                reject(err);
            });
        });
    }


    /**
     * send quest action
     * validate, check penalty
     * @param action 
     */
    checkAction(action: number, apply: boolean, questData: IActivityQuestSpecs): IQuestAction {
        if (!questData) {
            questData = this.questData;
        }
        return QuestUtils.checkAction(questData, this.questStatus, this.options, action, apply);
    }

    /**
     * apply retry penalty based on activity params and specs
     */
    applyRetryPenalty() {
        QuestUtils.applyCumulativeRetryPenalty(this.questData, this.questStatus, this.params);
    }

    checkInitialized() {
        return this.questStatus && this.questStatus.internal;
    }

    /**
     * submit response
     * update response / confirm response
     * resolve only
     * @param data 
     * @param resp 
     * @param confirm 
     */
    submitResponse(resp: IActivityQuestResponse, coords: ILatLng, test: boolean, questData: IActivityQuestSpecs): Promise<boolean> {
        return new Promise((resolve) => {
            if (!this.params && !test) {
                resolve(false);
                return;
            }

            if (!questData) {
                questData = this.questData;
            }

            if (test) {
                this.questData = questData;
            }

            let checkDynamicProm: Promise<boolean>;

            if (questData.dynamic) {
                switch (questData.type) {
                    case EQuestDynamicType.streetName:
                        checkDynamicProm = new Promise((resolve) => {
                            this.requestGeocodeStreetNameOptions(coords).then((streetNames: string[]) => {
                                questData.opt = streetNames;
                                resolve(true);
                            }).catch((err) => {
                                console.error(err);
                                // use default keyword as a fallback
                                resolve(null);
                            });
                        });
                        break;
                    default:
                        checkDynamicProm = Promise.resolve(null);
                        break;
                }
            } else {
                checkDynamicProm = Promise.resolve(null);
            }

            checkDynamicProm.then(() => {
                // check validate
                this.activityData.validateQuestData(questData, resp).then(async (match: boolean) => {
                    if (this.questStatus != null) {
                        this.questStatus.internal.questValidated = match;
                    }
                    resolve(match);
                }).catch((err) => {
                    console.error(err);
                    if (this.questStatus != null) {
                        this.questStatus.internal.questValidated = false;
                    }
                    resolve(false);
                });
            }).catch((err) => {
                console.error(err);
                if (this.questStatus != null) {
                    this.questStatus.internal.questValidated = false;
                }
                resolve(false);
            });
        });
    }

    async showValidateFeedback(match: boolean, questData: IActivityQuestSpecs) {
        let retryPenalty: number = 0;
        let cumulativePenalty: number = 0;
        let maxRetries: number = this.questStatus.internal.maxRetries;
        console.log("show validate feedback for quest: ", questData);

        if (!questData) {
            questData = this.questData;
        }
        let feedback: string = QuestUtils.getFeedback(questData, match, false);
        if (match) {
            await this.uiext.showAlert(Messages.msg.questCorrectAnswer.after.msg, Messages.msg.questCorrectAnswer.after.sub + "<p>" + feedback + "</p>", 1, null, true);
        } else {
            let retriesUsed: number = 0;
            let retriesLeft: number = 0;
            const isMultiQuest = this.multiQuestData != null && this.multiQuestData.length > 0;

            if (isMultiQuest) {
                // multi quest
                retryPenalty = QuestUtils.getRetryPenalty(questData, this.params);
                console.log("multi quest retry penalty: ", retryPenalty);
                console.log("max retries: ", maxRetries);

                this.questStatus.internal.retriesUsed += 1;
                retriesUsed = this.questStatus.internal.retriesUsed;
            } else {
                // single quest
                retryPenalty = QuestUtils.getRetryPenalty(questData, this.params);
                maxRetries = QuestUtils.getMaxRetry(questData, this.params);

                this.questStatus.internal.retriesUsed += 1;
                retriesUsed = this.questStatus.internal.retriesUsed;
            }

            const currentPenalty = this.questStatus != null ? this.questStatus.penalty : 0;
            if (isMultiQuest) {
                // Keep penalty-based failure aligned with total retries budget in multi quest.
                const remainingAttemptsIncludingCurrent = Math.max(1, maxRetries - (retriesUsed - 1));
                retryPenalty = Math.ceil((100 - currentPenalty) / remainingAttemptsIncludingCurrent);
                if (retryPenalty < 1) {
                    retryPenalty = 1;
                }
            }
            cumulativePenalty = currentPenalty + retryPenalty;
            if (cumulativePenalty > 100) {
                cumulativePenalty = 100;
            }

            if (isMultiQuest) {
                // multi quest: use total configured retries (sum of per-question retries)
                retriesLeft = Math.max(0, maxRetries - retriesUsed);
            } else {
                // single quest: based on actual fail condition and current penalty step
                if (retryPenalty > 0 && cumulativePenalty < 100) {
                    retriesLeft = Math.ceil((100 - cumulativePenalty) / retryPenalty);
                } else {
                    retriesLeft = 0;
                }
            }

            console.log("retry penalty for this attempt:", retryPenalty);
            console.log("retry penalty after this attempt:", cumulativePenalty);
            console.log("retries left after this attempt:", retriesLeft);
            console.log("retries used: ", retriesUsed);
            console.log("max retries: ", maxRetries);

            if (cumulativePenalty < 100) {
                await this.uiext.showAlert(Messages.msg.questWrongAnswer.after.msg, Messages.msg.questWrongAnswer.after.sub + `<b>${retriesLeft}</b>`,
                    1, null, true
                );
            } else {
                await this.uiext.showAlert(Messages.msg.questWrongAnswerFail.after.msg, Messages.msg.questWrongAnswerFail.after.sub + (feedback != null ? " " + feedback : ""),
                    1, null, true
                );
                match = true; // register activity complete after too many attempts
            }

            if (this.questStatus != null) {
                this.questStatus.penalty = cumulativePenalty;
            }
        }
    }

    async showValidateFeedbackBasic(questData: IActivityQuestSpecs, match: boolean, showCorrectAnswer: boolean) {
        let feedback: string = QuestUtils.getFeedback(questData, match, showCorrectAnswer);
        if (match) {
            // await this.uiext.showRewardPopupQueue("Got it!", feedback, null, false, 3000);
            await this.uiext.showAlert(Messages.msg.questCorrectAnswer.after.msg, Messages.msg.questCorrectAnswer.after.sub + "<p>" + feedback + "</p>", 1, null, true);
        }
        if (!match) {
            await this.uiext.showAlert(Messages.msg.questWrongAnswer.after.msg, feedback, 1, null, true);
        }
    }

    validate() {
        if (!this.checkInitialized()) {
            return;
        }
        this.questStatus.status = ECheckActivityResult.validated;
        this.statusObservable.next(this.questStatus);
    }

    confirmValidate() {
        if (!this.checkInitialized()) {
            return;
        }
        this.questStatus.internal.questValidatedConfirmed = true;
    }

    triggerFailed() {
        if (!this.checkInitialized()) {
            return;
        }
        this.questStatus.status = ECheckActivityResult.failed;
        this.statusObservable.next(this.questStatus);
    }

    checkConfirmValidateComplete() {
        if (!this.checkInitialized()) {
            return;
        }
        if (this.questStatus.internal.questValidated && this.questStatus.internal.questValidatedConfirmed) {
            // validate complete pending
            this.questStatus.status = ECheckActivityResult.done;
            this.statusObservable.next(this.questStatus);
        }
    }

    /**
     * handle timeout activity
     */
    initActivity(params: IQuestActivityDef, questData: IActivityQuestSpecs, multiQuestData: IActivityQuestSpecs[], previewOnly: boolean) {
        console.log(this.moduleName + "init");

        if (!this.subscription.timeoutActivityMonitor) {
            console.log(this.moduleName + "init started");

            if (multiQuestData != null && multiQuestData.length > 0) {
                this.questData = null;
                this.questData = multiQuestData[0]; // status aggregator
                this.multiQuestData = null;
                this.multiQuestData = multiQuestData;
            } else {
                this.questData = null;
                this.questData = questData;
            }

            this.params = params;

            this.questStatus = {
                tmData: null,
                status: ECheckActivityResult.inProgress,
                penalty: 0,
                usedClue: false,
                usedMapClue: false,
                internal: {
                    questValidated: false,
                    questValidatedConfirmed: false,
                    retryPenalty: 0,
                    maxRetries: 0,
                    retriesUsed: 0
                }
            };

            if (this.multiQuestData != null && this.multiQuestData.length > 0) {
                this.questStatus.internal.maxRetries = this.multiQuestData.reduce((sum: number, qd: IActivityQuestSpecs) => {
                    const retries: number = QuestUtils.getMaxRetry(qd, this.params);
                    return sum + (retries != null ? retries : 0);
                }, 0);
            } else {
                this.questStatus.internal.maxRetries = QuestUtils.getMaxRetry(this.questData, this.params);
            }

            console.log("init quest activity: ", this.questData);
            console.log("quest status: ", this.questStatus);

            if (previewOnly) {
                console.log("preview only / skipping timers init");
                return;
            }

            this.statusObservable.next(null);

            let tmParams: ITimeoutMonitorParams = {
                timeLimit: params.timeLimit
            };

            this.timeoutMonitor.start(tmParams);
            this.activityFinished = false;

            let setFailed = () => {
                this.questStatus.status = ECheckActivityResult.failed;
                this.statusObservable.next(this.questStatus);
                this.activityFinished = true;
            };

            console.log("setting up timers");

            this.subscription.timeoutActivityMonitor = this.timeoutMonitor.getWatch().subscribe((tmData: ITimeoutMonitorData) => {
                if (tmData) {

                    if (!tmData.isFallbackTimer) {
                        this.questStatus.tmData = tmData;
                    }

                    if (this.questStatus.internal.questValidated && this.questStatus.internal.questValidatedConfirmed) {
                        // quest validated, and picked up by the view processor
                        this.activityFinished = true;
                    }

                    switch (tmData.status) {
                        case ETimeoutStatus.expired:
                            setFailed();
                            break;
                        default:
                            break;
                    }

                    if (this.questStatus.penalty >= 100) {
                        setFailed();
                    }

                    if (this.activityFinished) {
                        this.subscription.timeoutActivityMonitor = ResourceManager.clearSub(this.subscription.timeoutActivityMonitor);
                        console.log("stopping timer");
                        this.timeoutMonitor.stop();
                    } else {
                        this.statusObservable.next(this.questStatus);
                    }
                }
            }, (err: Error) => {
                console.error(err);
            });
        } else {
            console.warn("timer already set");
        }
    }

    exitActivity() {
        console.log(this.moduleName + "exit");
        this.subscription = ResourceManager.clearSubObj(this.subscription);
        console.log("stopping timer");
        this.timeoutMonitor.stop();
        this.activityFinished = false;
    }
}
